import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    MachineProducedProductsDocument,
    MachineProducedProductsQuery,
    MachineProducedProductsQueryVariables,
} from '../../../../../services';

// Products each machine has actually run, most-produced first, cached per
// machine. Used to order a row's product picker so the realistic choices sit at
// the top — NOT to restrict it: a product a machine has never run is exactly
// what a new product is, and hiding it would make it unselectable.
//
// Fetched lazily by the expanded row only, since that is the one place a product
// picker exists.
export function useMachineProductsCache() {
    const client = useApolloClient();
    const [cache, setCache] = useState<Record<number, number[]>>({});
    const inFlight = useRef<Set<number>>(new Set());

    const ensure = useCallback(
        (machineId: number) => {
            if (inFlight.current.has(machineId)) return;
            setCache((prev) => {
                if (prev[machineId]) return prev;
                inFlight.current.add(machineId);
                void client
                    .query<
                        MachineProducedProductsQuery,
                        MachineProducedProductsQueryVariables
                    >({
                        query: MachineProducedProductsDocument,
                        variables: { machineId },
                        fetchPolicy: 'cache-first',
                    })
                    .then((res) => {
                        setCache((current) => ({
                            ...current,
                            [machineId]:
                                res.data?.getMachineProducedProductIds ?? [],
                        }));
                    })
                    .catch(() => {
                        // Non-fatal: the picker just keeps its default order.
                    })
                    .finally(() => {
                        inFlight.current.delete(machineId);
                    });
                return prev;
            });
        },
        [client],
    );

    const get = useCallback(
        (machineId: number): number[] | undefined => cache[machineId],
        [cache],
    );

    return { ensure, get };
}
